import { useRef, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { CheckCircle2, Circle, Users2, FileText, UserPlus, Upload, PartyPopper } from "lucide-react";
import { toast } from "sonner";

const STEPS = [
  { key: "basics",   title: "Community basics",  Icon: Circle,      hint: "Review the community you just created and adjust anything." },
  { key: "board",    title: "Seed the board",    Icon: Users2,      hint: "Invite your first 2-5 directors so they can vote and receive digests." },
  { key: "docs",     title: "Governing docs",    Icon: FileText,    hint: "Upload bylaws, CC&Rs, minutes — residents & directors can access on their portals." },
  { key: "residents",title: "Invite residents",  Icon: UserPlus,    hint: "Add residents individually or bulk-import a CSV." },
];

/**
 * CommunityOnboardingWizard — 4-step post-create flow that walks admins
 * through seeding a new community (basics → board → docs → residents).
 */
export function CommunityOnboardingWizard({ open, onOpenChange, community, onFinish }) {
  const [step, setStep] = useState(0);
  const [director, setDirector] = useState({ name: "", email: "" });
  const [directors, setDirectors] = useState([]);
  const [resident, setResident] = useState({ name: "", email: "", unit: "" });
  const [residents, setResidents] = useState([]);
  const [csvFile, setCsvFile] = useState(null);
  const [docFile, setDocFile] = useState(null);
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const csvRef = useRef(null);
  const docRef = useRef(null);

  if (!community) return null;

  const addDirector = async () => {
    if (!director.name || !director.email) return;
    setBusy(true);
    try {
      await api.post("/admin/users", {
        name: director.name, email: director.email, role: "board",
        property_ids: [community.id], send_invite: true,
      });
      setDirectors((d) => [...d, { ...director }]);
      setDirector({ name: "", email: "" });
      toast.success(`Invite emailed to ${director.email}`);
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed"); }
    finally { setBusy(false); }
  };

  const addResident = async () => {
    if (!resident.name || !resident.email) return;
    setBusy(true);
    try {
      await api.post("/admin/users", {
        name: resident.name, email: resident.email, role: "resident",
        property_ids: [community.id], unit: resident.unit || null,
        send_invite: true,
      });
      setResidents((r) => [...r, { ...resident }]);
      setResident({ name: "", email: "", unit: "" });
      toast.success(`Invite emailed to ${resident.email}`);
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed"); }
    finally { setBusy(false); }
  };

  const uploadResidentCsv = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/users/upload-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        params: { send_invites: true },
      });
      toast.success(`${data.created} residents imported`);
      setResidents((r) => [...r, ...Array(data.created).fill({ name: "(CSV row)", email: "—" })]);
    } catch (e) { toast.error(apiErrorMessage(e) || "Upload failed"); }
    finally { setBusy(false); if (csvRef.current) csvRef.current.value = ""; }
  };

  const uploadDoc = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("property_id", community.id);
      fd.append("visibility", "residents");
      const { data } = await api.post("/admin/documents/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDocs((d) => [...d, data.document]);
      toast.success(`Uploaded ${data.document.name}`);
    } catch (e) { toast.error(apiErrorMessage(e) || "Upload failed"); }
    finally { setBusy(false); if (docRef.current) docRef.current.value = ""; }
  };

  const advance = () => setStep((s) => Math.min(s + 1, STEPS.length));
  const finish = () => {
    onFinish?.();
    onOpenChange(false);
    setStep(0);
  };

  const currentStep = STEPS[step] || null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0b] border-white/10 text-white sm:max-w-[640px]" data-testid="community-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {step >= STEPS.length ? <PartyPopper className="h-4 w-4 text-emerald-400" /> : <Circle className="h-4 w-4 text-orange-400" />}
            {step >= STEPS.length ? "You're all set" : `Step ${step + 1} of ${STEPS.length} · ${currentStep?.title}`}
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            {step >= STEPS.length
              ? `${community.name} is ready to run. You can revisit any of these steps from the admin sidebar anytime.`
              : currentStep?.hint}
          </DialogDescription>
        </DialogHeader>

        {/* Progress rail */}
        <div className="flex items-center gap-1 mb-2">
          {STEPS.map((s, i) => (
            <div key={s.key} className="flex-1">
              <div className={"h-1 rounded-full " + (i < step ? "bg-emerald-500/70" : i === step ? "bg-orange-500" : "bg-white/10")} data-testid={`wizard-step-${s.key}`} />
              <div className={"mt-1 text-[10px] uppercase tracking-widest " +
                (i < step ? "text-emerald-300" : i === step ? "text-orange-300" : "text-zinc-600")}>
                {s.title}
              </div>
            </div>
          ))}
        </div>

        {/* Step content */}
        <div className="min-h-[220px]">
          {step === 0 && (
            <div className="space-y-2" data-testid="wizard-basics">
              <div className="rounded-md border border-white/10 bg-white/[0.02] p-3">
                <div className="text-[11px] uppercase tracking-widest text-zinc-500">Community</div>
                <div className="text-lg font-semibold">{community.name}</div>
                <div className="text-xs text-zinc-400 mt-1">
                  {community.units || "—"} units · {community.community_type || "hoa"} · {community.address || "no address"}
                </div>
              </div>
              <p className="text-xs text-zinc-500">
                Need to tweak stats? You can edit any time from Admin → Communities. Click <b>Next</b> to seed your board.
              </p>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-2" data-testid="wizard-board">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-zinc-400">Director name</Label>
                  <Input value={director.name} onChange={(e) => setDirector({ ...director, name: e.target.value })}
                         placeholder="Jane Director" className="bg-white/[0.04] border-white/10" data-testid="wizard-director-name" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Email</Label>
                  <Input type="email" value={director.email} onChange={(e) => setDirector({ ...director, email: e.target.value })}
                         placeholder="jane@…" className="bg-white/[0.04] border-white/10" data-testid="wizard-director-email" />
                </div>
              </div>
              <Button onClick={addDirector} disabled={busy || !director.name || !director.email}
                      size="sm" className="bg-violet-500 hover:bg-violet-600 rounded-full" data-testid="wizard-director-add">
                <UserPlus className="h-3 w-3 mr-1" /> Invite director
              </Button>
              {directors.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {directors.map((d, i) => (
                    <Badge key={i} variant="outline" className="border-emerald-500/40 text-emerald-300 text-[11px]" data-testid={`wizard-director-${i}`}>
                      <CheckCircle2 className="h-3 w-3 mr-1" /> {d.name} · {d.email}
                    </Badge>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-zinc-500 mt-2">Board members can be added later from Admin → Users too.</p>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-2" data-testid="wizard-docs">
              <input ref={docRef} type="file" hidden onChange={(e) => uploadDoc(e.target.files?.[0])}
                     accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.png,.jpg,.jpeg,.webp"
                     data-testid="wizard-doc-file-input" />
              <Button onClick={() => docRef.current?.click()} disabled={busy}
                      size="sm" className="bg-orange-500 hover:bg-orange-600 rounded-full" data-testid="wizard-doc-pick">
                <Upload className="h-3 w-3 mr-1" /> {busy ? "Uploading…" : "Upload governing doc"}
              </Button>
              <p className="text-[11px] text-zinc-500">Bylaws, CC&amp;Rs, minutes — anything residents should have on hand. Files are visible to residents by default.</p>
              {docs.length > 0 && (
                <div className="mt-2 space-y-1">
                  {docs.map((d) => (
                    <div key={d.id} className="rounded-md border border-white/10 px-2.5 py-1.5 text-xs flex items-center gap-2" data-testid={`wizard-doc-${d.id}`}>
                      <FileText className="h-3 w-3 text-emerald-400" />
                      <span className="truncate">{d.name}</span>
                      <span className="text-zinc-500 ml-auto">{d.mime.split("/").pop()}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3" data-testid="wizard-residents">
              <div className="grid grid-cols-2 gap-2">
                <div className="col-span-2">
                  <Label className="text-xs text-zinc-400">Or bulk-import via CSV</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input ref={csvRef} type="file" hidden accept=".csv"
                           onChange={(e) => uploadResidentCsv(e.target.files?.[0])}
                           data-testid="wizard-csv-input" />
                    <Button onClick={() => csvRef.current?.click()} disabled={busy}
                            size="sm" variant="outline" className="border-white/10 rounded-full"
                            data-testid="wizard-csv-pick">
                      <Upload className="h-3 w-3 mr-1" /> {busy ? "Uploading…" : "Choose CSV"}
                    </Button>
                    <a href={`/api/admin/users/import-template.csv`} className="text-[11px] text-orange-300">Template</a>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Or add one</Label>
                  <Input value={resident.name} onChange={(e) => setResident({ ...resident, name: e.target.value })}
                         placeholder="Full name" className="bg-white/[0.04] border-white/10" data-testid="wizard-resident-name" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400 opacity-0">.</Label>
                  <Input type="email" value={resident.email} onChange={(e) => setResident({ ...resident, email: e.target.value })}
                         placeholder="Email" className="bg-white/[0.04] border-white/10" data-testid="wizard-resident-email" />
                </div>
                <Input value={resident.unit} onChange={(e) => setResident({ ...resident, unit: e.target.value })}
                       placeholder="Unit (optional)" className="bg-white/[0.04] border-white/10 col-span-2" data-testid="wizard-resident-unit" />
              </div>
              <Button onClick={addResident} disabled={busy || !resident.name || !resident.email}
                      size="sm" className="bg-emerald-500 hover:bg-emerald-600 rounded-full" data-testid="wizard-resident-add">
                <UserPlus className="h-3 w-3 mr-1" /> Invite resident
              </Button>
              {residents.length > 0 && (
                <div className="text-[11px] text-emerald-300 mt-1" data-testid="wizard-resident-count">
                  <CheckCircle2 className="h-3 w-3 inline mr-1" /> {residents.length} resident{residents.length === 1 ? "" : "s"} added
                </div>
              )}
            </div>
          )}

          {step >= STEPS.length && (
            <div className="text-center py-6" data-testid="wizard-finished">
              <PartyPopper className="h-10 w-10 mx-auto text-emerald-400 mb-2" />
              <div className="text-lg font-semibold">Community is live 🎉</div>
              <div className="text-xs text-zinc-500 mt-1">Directors and residents have been invited. Everyone can log in as soon as they set their password.</div>
              <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
                <div className="rounded-md border border-white/10 p-2">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Directors</div>
                  <div className="text-lg tabular-nums text-white">{directors.length}</div>
                </div>
                <div className="rounded-md border border-white/10 p-2">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Docs</div>
                  <div className="text-lg tabular-nums text-white">{docs.length}</div>
                </div>
                <div className="rounded-md border border-white/10 p-2">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Residents</div>
                  <div className="text-lg tabular-nums text-white">{residents.length}</div>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="pt-2">
          {step > 0 && step < STEPS.length && (
            <Button variant="outline" onClick={() => setStep((s) => s - 1)} disabled={busy} className="border-white/10" data-testid="wizard-back">
              Back
            </Button>
          )}
          {step < STEPS.length ? (
            <Button onClick={advance} disabled={busy} className="bg-orange-500 hover:bg-orange-600" data-testid="wizard-next">
              {step === STEPS.length - 1 ? "Finish" : "Next"}
            </Button>
          ) : (
            <Button onClick={finish} className="bg-emerald-500 hover:bg-emerald-600" data-testid="wizard-done">
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
